import * as Sentry from '@sentry/nuxt';
import packageJson from './package.json';
import { HekateExternalUtil } from '@onshape/www-inertia';

const anonymousUserErrorSampleRate = 0.01;

const isWistiaError = (event: Sentry.Event) =>
  event.exception?.values?.find((val) =>
    val.stacktrace?.frames?.find((frame) => frame.abs_path?.includes('wistia.com'))
  );

const shouldDropAnonymousUserError = ({ event }: { event: Sentry.ErrorEvent }): boolean => {
  if (event.user?.id) {
    return false;
  }

  const isDropped = Math.random() > anonymousUserErrorSampleRate;
  if (isDropped) {
    console.warn(
      `SentryClientConfig - Dropping anonymous user error: ${event.exception?.values?.[0]?.value} - ${event.exception?.values?.[0]?.type}`
    );
  }
  return isDropped;
};

const env = HekateExternalUtil.isProduction() ? 'production' : 'dev';

Sentry.init({
  dsn: 'https://383d88fdab7b4a129156980bde0511cb@o523154.ingest.sentry.io/5635145',
  release: `www-onshape@${packageJson.version}`,
  integrations: [],
  denyUrls: [/wistia\.com/i, /engagio\.com/i, /cookielaw\.org/i, /taboola\.com/i],
  tracesSampleRate: 0.005,
  environment: env,
  ignoreErrors: ['E-v1'],
  beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
    if (!window) {
      return null;
    }

    if (shouldDropAnonymousUserError({ event })) {
      return null;
    }

    if (isWistiaError(event)) {
      return null;
    }

    if (!hint || !hint.originalException) {
      return event;
    }
    const error = hint.originalException;

    let errorMessage = '';
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    if (errorMessage.includes('timeout') || errorMessage.includes('error')) {
      return null;
    }

    if (
      window.navigator &&
      window.navigator.userAgent &&
      window.navigator.userAgent.toLocaleLowerCase().includes('onetrust') &&
      Math.random() > 0.01
    ) {
      return null;
    }

    if (errorMessage && errorMessage.match('TypeError: Failed to fetch') && Math.random() > 0.01) {
      return null;
    }

    if (
      errorMessage &&
      errorMessage.match(
        /Object Not Found Matching Id:[0-9]|Extension context invalidated|popoverBorderWidth|Cannot read property '__k' of undefined|Unexpected token '<'|Got an unsuccessful status code: 0/gi
      ) &&
      Math.random() > 0.001
    ) {
      return null;
    }

    return event;
  },
  attachStacktrace: true,
});
